import React, { useEffect, useMemo, useState } from "react";
import { injectIntl } from "react-intl";
import {
  Alert,
  Button,
  Col,
  FormGroup,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Row
} from "reactstrap";
import IntlMessages from "../../../helpers/IntlMessages";
import ShopeeV2Api from "../../../services/shopeeV2";
import {
  defaultShopeeListingShopId,
  isConcreteShopeeShopId,
  isPrimaryShopeeShop,
  loadShopeeListingEditorData,
  shopeeEditorErrorMessage
} from "./shopeeListingEditorData";

const T = ({ id }) => <IntlMessages id={id} />;

const emptyForm = {
  product_id: "",
  item_name: "",
  description: "",
  original_price: "",
  weight: "",
  category_id: "",
  brand_id: "",
  brand_name: "",
  condition: "NEW",
  item_status: "NORMAL",
  package_length: "",
  package_width: "",
  package_height: "",
  image_urls: ""
};

const logisticId = channel =>
  Number(channel.logistic_id || channel.logistics_channel_id || 0);

const logisticName = channel =>
  channel.logistic_name ||
  channel.logistics_channel_name ||
  channel.channel_name ||
  `#${logisticId(channel)}`;

const attributeName = attribute =>
  attribute.display_attribute_name ||
  attribute.original_attribute_name ||
  attribute.attribute_name ||
  `#${attribute.attribute_id}`;

const attributeValueName = value =>
  value.display_value_name ||
  value.original_value_name ||
  value.value_name ||
  `#${value.value_id}`;

const brandName = brand =>
  brand.display_brand_name ||
  brand.original_brand_name ||
  brand.brand_name ||
  `#${brand.brand_id}`;

const modelPrice = model =>
  Number(
    model.original_price !== undefined
      ? model.original_price
      : model.price_info &&
          model.price_info[0] &&
          model.price_info[0].original_price
  ) || 0;

function categoryLabels(categories) {
  const byId = (categories || []).reduce((map, category) => {
    map[String(category.category_id)] = category;
    return map;
  }, {});
  return (categories || []).reduce((map, category) => {
    const names = [];
    let current = category;
    let safety = 0;
    while (current && safety < 5) {
      names.unshift(
        current.display_category_name ||
          current.original_category_name ||
          current.category_name ||
          `#${current.category_id}`
      );
      current = byId[String(current.parent_category_id || "")];
      safety += 1;
    }
    map[String(category.category_id)] = names.join(" › ");
    return map;
  }, {});
}

function currentAttributeValues(item) {
  return ((item && item.attribute_list) || []).reduce((values, attribute) => {
    const first = attribute.attribute_value_list && attribute.attribute_value_list[0];
    if (!first) return values;
    values[String(attribute.attribute_id)] =
      Number(first.value_id || 0) > 0
        ? String(first.value_id)
        : first.original_value_name || first.value_name || "";
    return values;
  }, {});
}

function ShopeeListingEditor({
  intl,
  mode,
  mapping,
  shops = [],
  initialShopId,
  isOpen,
  onClose,
  runAction,
  busy
}) {
  const isUpdate = mode === "update";
  const [shopId, setShopId] = useState(() =>
    defaultShopeeListingShopId(shops, initialShopId)
  );
  const hasConcreteShop = isConcreteShopeeShopId(shopId);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [loadIssues, setLoadIssues] = useState([]);
  const [primaryLoadFailed, setPrimaryLoadFailed] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  const [form, setForm] = useState(emptyForm);
  const [candidates, setCandidates] = useState([]);
  const [categories, setCategories] = useState([]);
  const [attributes, setAttributes] = useState([]);
  const [attributeValues, setAttributeValues] = useState({});
  const [brands, setBrands] = useState([]);
  const [brandMandatory, setBrandMandatory] = useState(false);
  const [brandsLoading, setBrandsLoading] = useState(false);
  const [brandLoadFailed, setBrandLoadFailed] = useState(false);
  const [logistics, setLogistics] = useState([]);
  const [selectedLogistics, setSelectedLogistics] = useState([]);
  const [models, setModels] = useState([]);
  const [imageUrlsDirty, setImageUrlsDirty] = useState(false);
  const categoryLabelMap = useMemo(() => categoryLabels(categories), [categories]);
  const actionKey = isUpdate
    ? `listing-update-${mapping && mapping.platform_item_id}`
    : "listing-publish";
  let brandPlaceholder = "—";
  if (brandsLoading) {
    brandPlaceholder = intl
      ? intl.formatMessage({
          id: "shopee.workspace.common.loading",
          defaultMessage: "Đang tải..."
        })
      : "Đang tải...";
  }

  useEffect(() => {
    if (!isOpen) return;
    setShopId(current =>
      defaultShopeeListingShopId(
        shops,
        isConcreteShopeeShopId(current) ? current : initialShopId
      )
    );
  }, [initialShopId, isOpen, shops]);

  useEffect(() => {
    if (!isOpen) return undefined;
    const controller = new AbortController();
    setError("");
    setLoadIssues([]);
    setPrimaryLoadFailed(false);
    setForm(emptyForm);
    setCandidates([]);
    setCategories([]);
    setAttributes([]);
    setAttributeValues({});
    setBrands([]);
    setBrandMandatory(false);
    setBrandsLoading(false);
    setBrandLoadFailed(false);
    setLogistics([]);
    setSelectedLogistics([]);
    setModels([]);
    setImageUrlsDirty(false);

    if (!hasConcreteShop) {
      setLoading(false);
      setPrimaryLoadFailed(true);
      return () => controller.abort();
    }

    setLoading(true);

    const load = async () => {
      try {
        const result = await loadShopeeListingEditorData({
          api: ShopeeV2Api,
          shopId,
          isUpdate,
          itemId: mapping && mapping.platform_item_id,
          signal: controller.signal
        });
        if (controller.signal.aborted) return;
        const nextCategories = result.categories;
        const nextLogistics = result.logistics;
        setCategories(nextCategories);
        setLogistics(nextLogistics);
        setLoadIssues(result.errors);
        setPrimaryLoadFailed(result.primaryFailed);
        if (isUpdate && !result.primaryFailed) {
          const detail = result.primary || {};
          const item = detail.item || {};
          const nextModels = detail.models || [];
          const imageUrls =
            (item.image && item.image.image_url_list) ||
            item.image_url_list ||
            [];
          setModels(
            nextModels.map(model => ({
              model_id: Number(model.model_id || 0),
              model_sku: model.model_sku || `#${model.model_id}`,
              original_price: String(modelPrice(model))
            }))
          );
          setForm({
            product_id: "",
            item_name: item.item_name || "",
            description: item.description || "",
            original_price: String(modelPrice(item) || ""),
            weight: String(item.weight || ""),
            category_id: String(item.category_id || ""),
            brand_id:
              item.brand && item.brand.brand_id !== undefined
                ? String(item.brand.brand_id)
                : "",
            brand_name:
              (item.brand &&
                (item.brand.original_brand_name ||
                  item.brand.display_brand_name)) ||
              "",
            condition: item.condition || "NEW",
            item_status: item.item_status || "NORMAL",
            package_length: String(
              (item.dimension && item.dimension.package_length) || ""
            ),
            package_width: String(
              (item.dimension && item.dimension.package_width) || ""
            ),
            package_height: String(
              (item.dimension && item.dimension.package_height) || ""
            ),
            image_urls: imageUrls.join("\n")
          });
          setAttributeValues(currentAttributeValues(item));
          setSelectedLogistics(
            (item.logistic_info || [])
              .filter(entry => entry.enabled !== false)
              .map(entry => Number(entry.logistic_id))
          );
        } else if (!isUpdate && !result.primaryFailed) {
          setCandidates(result.primary || []);
          setSelectedLogistics(nextLogistics.map(logisticId).filter(Boolean));
        }
      } catch (loadError) {
        if (
          loadError.name !== "CanceledError" &&
          loadError.code !== "ERR_CANCELED"
        ) {
          setPrimaryLoadFailed(true);
          setError(shopeeEditorErrorMessage(loadError));
        }
      } finally {
        if (!controller.signal.aborted) setLoading(false);
      }
    };
    load();
    return () => controller.abort();
  }, [hasConcreteShop, isOpen, isUpdate, mapping, reloadKey, shopId]);

  useEffect(() => {
    if (!isOpen || !hasConcreteShop || !form.category_id) {
      setAttributes([]);
      return undefined;
    }
    const controller = new AbortController();
    ShopeeV2Api.catalogAttributes(
      shopId,
      form.category_id,
      controller.signal
    )
      .then(response => setAttributes(response.data || []))
      .catch(loadError => {
        if (
          loadError.name !== "CanceledError" &&
          loadError.code !== "ERR_CANCELED"
        ) {
          setError(
            (loadError.response &&
              loadError.response.data &&
              loadError.response.data.error &&
              loadError.response.data.error.message) ||
              loadError.message
          );
        }
      });
    return () => controller.abort();
  }, [form.category_id, hasConcreteShop, isOpen, shopId]);

  useEffect(() => {
    if (!isOpen || !hasConcreteShop || !form.category_id) {
      setBrands([]);
      setBrandMandatory(false);
      setBrandsLoading(false);
      setBrandLoadFailed(false);
      return undefined;
    }
    const controller = new AbortController();
    setBrandsLoading(true);
    setBrandLoadFailed(false);
    ShopeeV2Api.catalogBrands(shopId, form.category_id, controller.signal)
      .then(response => {
        const data = response.data || {};
        setBrands(data.brands || []);
        setBrandMandatory(Boolean(data.is_mandatory));
        setBrandLoadFailed(false);
      })
      .catch(loadError => {
        if (
          loadError.name !== "CanceledError" &&
          loadError.code !== "ERR_CANCELED"
        ) {
          setBrands([]);
          setBrandMandatory(false);
          setBrandLoadFailed(true);
          setError(shopeeEditorErrorMessage(loadError));
        }
      })
      .finally(() => {
        if (!controller.signal.aborted) setBrandsLoading(false);
      });
    return () => controller.abort();
  }, [form.category_id, hasConcreteShop, isOpen, shopId]);

  const setField = (field, value) =>
    setForm(current => ({ ...current, [field]: value }));

  const selectProduct = productId => {
    const product = candidates.find(
      candidate => String(candidate.id) === String(productId)
    );
    if (!product) {
      setForm(emptyForm);
      return;
    }
    const salePrice =
      product.active_sale && Number(product.price_sale) > 0
        ? product.price_sale
        : product.price;
    setForm(current => ({
      ...current,
      product_id: String(product.id),
      item_name: product.name || "",
      description: intl
        ? intl.formatMessage(
            {
              id: "shopee.workspace.listing.description-template",
              defaultMessage: "{name} - Sản phẩm chất lượng cao"
            },
            { name: product.name || "" }
          )
        : `${product.name || ""} - Sản phẩm chất lượng cao`,
      original_price: String(salePrice || ""),
      weight: String(product.weight || ""),
      package_length: String(product.length || ""),
      package_width: String(product.width || ""),
      package_height: String(product.height || "")
    }));
  };

  const toggleLogistic = id => {
    setSelectedLogistics(current =>
      current.includes(id)
        ? current.filter(value => value !== id)
        : [...current, id]
    );
  };

  const buildAttributes = () =>
    Object.keys(attributeValues)
      .filter(key => String(attributeValues[key] || "").trim())
      .map(key => {
        const attribute = attributes.find(
          entry => String(entry.attribute_id) === String(key)
        );
        const value = attributeValues[key];
        const dropdown =
          attribute &&
          (attribute.input_type === "DROP_DOWN" ||
            (attribute.attribute_value_list || []).length > 0);
        return {
          attribute_id: Number(key),
          attribute_value_list: dropdown
            ? [{ value_id: Number(value) }]
            : [{ value_id: 0, original_value_name: String(value).trim() }]
        };
      });

  const validate = () => {
    const errors = [];
    if (!hasConcreteShop)
      errors.push(
        intl
          ? intl.formatMessage({
              id: "shopee.workspace.select-shop-for-listing",
              defaultMessage: "Chưa chọn gian hàng Shopee"
            })
          : "Chưa chọn gian hàng Shopee"
      );
    if (!isUpdate && !Number(form.product_id))
      errors.push(
        intl
          ? intl.formatMessage({
              id: "shopee.push.step.select",
              defaultMessage: "Chưa chọn sản phẩm VMASS"
            })
          : "Chưa chọn sản phẩm VMASS"
      );
    if (form.item_name.trim().length < 5 || form.item_name.trim().length > 120) {
      errors.push(
        intl
          ? intl.formatMessage({
              id: "shopee.push.validate.name",
              defaultMessage: "Tên sản phẩm phải từ 5-120 ký tự"
            })
          : "Tên sản phẩm phải từ 5-120 ký tự"
      );
    }
    if (form.description.trim().length < 25) {
      errors.push(
        intl
          ? intl.formatMessage({
              id: "shopee.push.validate.description",
              defaultMessage: "Mô tả phải có ít nhất 25 ký tự"
            })
          : "Mô tả phải có ít nhất 25 ký tự"
      );
    }
    if (!models.length && Number(form.original_price) <= 0) {
      errors.push(
        intl
          ? intl.formatMessage({
              id: "shopee.push.validate.price",
              defaultMessage: "Giá phải lớn hơn 0"
            })
          : "Giá phải lớn hơn 0"
      );
    }
    if (models.some(model => Number(model.original_price) <= 0)) {
      errors.push(
        intl
          ? intl.formatMessage({
              id: "shopee.workspace.listing.err.variant-price",
              defaultMessage: "Giá của mọi biến thể phải lớn hơn 0"
            })
          : "Giá của mọi biến thể phải lớn hơn 0"
      );
    }
    if (Number(form.weight) < 0.01 || Number(form.weight) > 50) {
      errors.push(
        intl
          ? intl.formatMessage({
              id: "shopee.push.validate.weight",
              defaultMessage: "Cân nặng phải từ 0.01 đến 50 kg"
            })
          : "Cân nặng phải từ 0.01 đến 50 kg"
      );
    }
    if (!Number(form.category_id))
      errors.push(
        intl
          ? intl.formatMessage({
              id: "shopee.push.validate.category",
              defaultMessage: "Chưa chọn danh mục Shopee"
            })
          : "Chưa chọn danh mục Shopee"
      );
    if (brandLoadFailed)
      errors.push(
        intl
          ? intl.formatMessage({
              id: "shopee.workspace.listing.err.brand-load-failed",
              defaultMessage: "Chưa tải được thương hiệu Shopee"
            })
          : "Chưa tải được thương hiệu Shopee"
      );
    if (brandMandatory && form.brand_id === "") {
      errors.push(
        intl
          ? intl.formatMessage({
              id: "shopee.workspace.listing.err.no-brand",
              defaultMessage: "Chưa chọn thương hiệu Shopee"
            })
          : "Chưa chọn thương hiệu Shopee"
      );
    }
    if (!selectedLogistics.length)
      errors.push(
        intl
          ? intl.formatMessage({
              id: "shopee.push.validate.logistics",
              defaultMessage: "Chưa chọn kênh vận chuyển"
            })
          : "Chưa chọn kênh vận chuyển"
      );
    attributes
      .filter(attribute => attribute.is_mandatory)
      .forEach(attribute => {
        if (!String(attributeValues[attribute.attribute_id] || "").trim()) {
          errors.push(
            intl
              ? intl.formatMessage(
                  {
                    id: "shopee.workspace.listing.err.missing-attribute",
                    defaultMessage: "Thiếu thuộc tính: {attribute}"
                  },
                  { attribute: attributeName(attribute) }
                )
              : `Thiếu thuộc tính: ${attributeName(attribute)}`
          );
        }
      });
    const dimensions = [
      form.package_length,
      form.package_width,
      form.package_height
    ];
    if (
      dimensions.some(Boolean) &&
      dimensions.some(value => !Number.isFinite(Number(value)) || Number(value) <= 0)
    ) {
      errors.push(
        intl
          ? intl.formatMessage({
              id: "shopee.workspace.listing.err.dimensions",
              defaultMessage: "Cần nhập đủ ba kích thước lớn hơn 0"
            })
          : "Cần nhập đủ ba kích thước lớn hơn 0"
      );
    }
    return errors;
  };

  const submit = async () => {
    const errors = validate();
    if (errors.length) {
      setError(errors.join("; "));
      return;
    }
    setError("");
    const common = {
      item_name: form.item_name.trim(),
      description: form.description.trim(),
      weight: Number(form.weight),
      category_id: Number(form.category_id),
      condition: form.condition,
      logistic_info: selectedLogistics.map(id => ({
        logistic_id: Number(id),
        enabled: true
      })),
      attribute_list: buildAttributes(),
      ...(form.brand_id !== ""
        ? {
            brand: {
              brand_id: Number(form.brand_id),
              original_brand_name: form.brand_name
            }
          }
        : {})
    };
    const hasDimensions =
      form.package_length && form.package_width && form.package_height;
    let response;
    if (isUpdate) {
      const payload = {
        ...common,
        ...(hasDimensions
          ? {
              dimension: {
                package_length: Number(form.package_length),
                package_width: Number(form.package_width),
                package_height: Number(form.package_height)
              }
            }
          : {}),
        price_list: models.length
          ? models.map(model => ({
              model_id: Number(model.model_id),
              original_price: Number(model.original_price)
            }))
          : [{ model_id: 0, original_price: Number(form.original_price) }],
        ...(imageUrlsDirty
          ? {
              image_urls: form.image_urls
                .split(/\r?\n|,/)
                .map(value => value.trim())
                .filter(Boolean)
            }
          : {})
      };
      response = await runAction(
        actionKey,
        key =>
          ShopeeV2Api.updateListing(
            shopId,
            mapping.platform_item_id,
            payload,
            key
          ),
        intl
          ? intl.formatMessage({
              id: "shopee.workspace.listing.update-queued",
              defaultMessage: "Đã đưa yêu cầu cập nhật listing vào hàng đợi"
            })
          : "Đã đưa yêu cầu cập nhật listing vào hàng đợi"
      );
    } else {
      const payload = {
        ...common,
        original_price: Number(form.original_price),
        item_status: form.item_status,
        ...(hasDimensions
          ? {
              package_length: Number(form.package_length),
              package_width: Number(form.package_width),
              package_height: Number(form.package_height)
            }
          : {})
      };
      response = await runAction(
        actionKey,
        key =>
          ShopeeV2Api.publishListing(
            {
              shop_id: shopId,
              product_id: Number(form.product_id),
              shopee_data: payload
            },
            key
          ),
        intl
          ? intl.formatMessage({
              id: "shopee.workspace.listing.publish-queued",
              defaultMessage: "Đã đưa sản phẩm vào hàng đợi đăng Shopee"
            })
          : "Đã đưa sản phẩm vào hàng đợi đăng Shopee"
      );
    }
    if (response) onClose();
  };

  const leafCategories = categories.filter(
    category => !category.has_children && !category.has_child
  );
  const supportDataMissing =
    hasConcreteShop && !loading && (!categories.length || !logistics.length);
  const showLoadProblem =
    hasConcreteShop &&
    !loading &&
    (primaryLoadFailed || supportDataMissing || brandLoadFailed || loadIssues.length > 0);
  const submitDisabled =
    loading ||
    busy ||
    !hasConcreteShop ||
    primaryLoadFailed ||
    supportDataMissing ||
    brandsLoading ||
    brandLoadFailed ||
    (!isUpdate && !candidates.length);

  return (
    <Modal isOpen={isOpen} toggle={onClose} size="lg">
      <ModalHeader toggle={onClose}>
        <T
          id={
            isUpdate
              ? "shopee.workspace.update-listing"
              : "shopee.push.modal.title"
          }
        />
      </ModalHeader>
      <ModalBody>
        <FormGroup>
          <Label>
            <T id="shopee.workspace.listing.label.shop" />
          </Label>
          <Input
            type="select"
            value={shopId}
            disabled={Boolean(busy) || loading || shops.length < 2}
            onChange={event => setShopId(event.target.value)}
          >
            {!shops.length ? (
              <option value="">
                {intl
                  ? intl.formatMessage({
                      id: "shopee.workspace.listing.no-shops",
                      defaultMessage: "Chưa có gian hàng Shopee"
                    })
                  : "Chưa có gian hàng Shopee"}
              </option>
            ) : null}
            {shops.map(shop => {
              const shopLabel =
                shop.shop_name ||
                (intl
                  ? intl.formatMessage(
                      {
                        id: "shopee.workspace.overview.shop-label",
                        defaultMessage: "Shop #{shopId}"
                      },
                      { shopId: shop.shop_id }
                    )
                  : `Shop ${shop.shop_id}`);
              const primaryMark = isPrimaryShopeeShop(shop)
                ? ` · ${
                    intl
                      ? intl.formatMessage({
                          id: "shopee.workspace.listing.primary-mark",
                          defaultMessage: "Chính"
                        })
                      : "Chính"
                  }`
                : "";
              const regionMark = shop.shop_region ? ` · ${shop.shop_region}` : "";
              return (
                <option key={shop.shop_id} value={shop.shop_id}>
                  {`${shopLabel}${primaryMark}${regionMark}`}
                </option>
              );
            })}
          </Input>
          <small className="form-text text-muted">
            <T id="shopee.workspace.listing.shop-hint" />
          </small>
        </FormGroup>
        {error ? <Alert color="danger">{error}</Alert> : null}
        {!hasConcreteShop ? (
          <Alert color="warning">
            <T id="shopee.workspace.select-shop-for-listing" />
          </Alert>
        ) : null}
        {showLoadProblem ? (
          <Alert color={primaryLoadFailed ? "danger" : "warning"}>
            <div><T id="shopee.workspace.listing-load-incomplete" /></div>
            {loadIssues.length ? (
              <ul className="mb-2 mt-2 pl-4">
                {loadIssues.map(issue => (
                  <li key={`${issue.source}-${issue.message}`}>{issue.message}</li>
                ))}
              </ul>
            ) : null}
            <Button
              color="link"
              className="p-0"
              onClick={() => setReloadKey(value => value + 1)}
            >
              <T id="shopee.workspace.retry" />
            </Button>
          </Alert>
        ) : null}
        {!loading &&
        hasConcreteShop &&
        !isUpdate &&
        !primaryLoadFailed &&
        !candidates.length ? (
          <Alert color="info">
            <T id="shopee.workspace.listing-no-candidates" />
          </Alert>
        ) : null}
        {loading ? (
          <div className="text-center py-5">
            <div className="spinner-border text-primary" role="status" />
          </div>
        ) : hasConcreteShop && !primaryLoadFailed ? (
          <React.Fragment>
            {!isUpdate ? (
              <FormGroup>
                <Label><T id="shopee.push.step.select" /> *</Label>
                <Input
                  type="select"
                  value={form.product_id}
                  onChange={event => selectProduct(event.target.value)}
                >
                  <option value="">—</option>
                  {candidates.map(product => (
                    <option key={product.id} value={product.id}>
                      {product.name} ({product.sku})
                    </option>
                  ))}
                </Input>
              </FormGroup>
            ) : null}
            <FormGroup>
              <Label><T id="shopee.push.form.name" /> *</Label>
              <Input
                value={form.item_name}
                maxLength={120}
                onChange={event => setField("item_name", event.target.value)}
              />
            </FormGroup>
            <FormGroup>
              <Label><T id="shopee.push.form.description" /> *</Label>
              <Input
                type="textarea"
                rows="5"
                maxLength={5000}
                value={form.description}
                onChange={event => setField("description", event.target.value)}
              />
            </FormGroup>
            <Row>
              <Col md="4">
                <FormGroup>
                  <Label><T id="shopee.push.form.price" /> *</Label>
                  <Input
                    type="number"
                    min="1"
                    disabled={models.length > 0}
                    value={form.original_price}
                    onChange={event =>
                      setField("original_price", event.target.value)
                    }
                  />
                </FormGroup>
              </Col>
              <Col md="4">
                <FormGroup>
                  <Label><T id="shopee.push.form.weight" /> *</Label>
                  <Input
                    type="number"
                    min="0.01"
                    max="50"
                    step="0.01"
                    value={form.weight}
                    onChange={event => setField("weight", event.target.value)}
                  />
                </FormGroup>
              </Col>
              <Col md="4">
                <FormGroup>
                  <Label><T id="shopee.push.form.condition" /></Label>
                  <Input
                    type="select"
                    value={form.condition}
                    onChange={event => setField("condition", event.target.value)}
                  >
                    <option value="NEW">NEW</option>
                    <option value="USED">USED</option>
                  </Input>
                </FormGroup>
              </Col>
            </Row>
            {models.length ? (
              <div className="mb-3">
                <Label><T id="shopee.workspace.variant-prices" /></Label>
                {models.map((model, index) => (
                  <Row className="mb-2" key={model.model_id}>
                    <Col md="6" className="pt-2">{model.model_sku}</Col>
                    <Col md="6">
                      <Input
                        type="number"
                        min="1"
                        value={model.original_price}
                        onChange={event =>
                          setModels(current =>
                            current.map((entry, modelIndex) =>
                              modelIndex === index
                                ? {
                                    ...entry,
                                    original_price: event.target.value
                                  }
                                : entry
                            )
                          )
                        }
                      />
                    </Col>
                  </Row>
                ))}
              </div>
            ) : null}
            <FormGroup>
              <Label><T id="shopee.push.form.category" /> *</Label>
              <Input
                type="select"
                value={form.category_id}
                onChange={event => {
                  setForm(current => ({
                    ...current,
                    category_id: event.target.value,
                    brand_id: "",
                    brand_name: ""
                  }));
                  setAttributeValues({});
                }}
              >
                <option value="">—</option>
                {(leafCategories.length ? leafCategories : categories).map(
                  category => (
                    <option
                      key={category.category_id}
                      value={category.category_id}
                    >
                      {categoryLabelMap[String(category.category_id)]}
                    </option>
                  )
                )}
              </Input>
            </FormGroup>
            {form.category_id ? (
              <FormGroup>
                <Label>
                  <T id="shopee.workspace.listing.label.brand" /> {brandMandatory ? "*" : ""}
                </Label>
                <Input
                  type="select"
                  value={form.brand_id}
                  disabled={brandsLoading}
                  onChange={event => {
                    const selected = brands.find(
                      brand => String(brand.brand_id) === event.target.value
                    );
                    setForm(current => ({
                      ...current,
                      brand_id: event.target.value,
                      brand_name: selected
                        ? selected.original_brand_name || brandName(selected)
                        : ""
                    }));
                  }}
                >
                  <option value="">{brandPlaceholder}</option>
                  {form.brand_id !== "" &&
                  !brands.some(brand => String(brand.brand_id) === form.brand_id) ? (
                    <option value={form.brand_id}>
                      {form.brand_name || `#${form.brand_id}`}
                    </option>
                  ) : null}
                  {brands.map(brand => (
                    <option key={brand.brand_id} value={brand.brand_id}>
                      {brandName(brand)}
                    </option>
                  ))}
                </Input>
              </FormGroup>
            ) : null}
            <Row>
              {["package_length", "package_width", "package_height"].map(field => (
                <Col md="4" key={field}>
                  <FormGroup>
                    <Label>
                      <T id={`shopee.workspace.${field.replace("package_", "")}`} />
                    </Label>
                    <Input
                      type="number"
                      min="1"
                      value={form[field]}
                      onChange={event => setField(field, event.target.value)}
                    />
                  </FormGroup>
                </Col>
              ))}
            </Row>
            <FormGroup>
              <Label><T id="shopee.push.form.logistics" /> *</Label>
              <div>
                {logistics.map(channel => {
                  const id = logisticId(channel);
                  return (
                    <Label className="mr-4" key={id}>
                      <Input
                        type="checkbox"
                        checked={selectedLogistics.includes(id)}
                        onChange={() => toggleLogistic(id)}
                      />{" "}
                      {logisticName(channel)}
                    </Label>
                  );
                })}
              </div>
            </FormGroup>
            {attributes.length ? (
              <div className="mb-3">
                <Label><T id="shopee.push.form.attributes" /></Label>
                <Row>
                  {attributes
                    .filter(attribute => attribute.is_mandatory)
                    .map(attribute => {
                      const values = attribute.attribute_value_list || [];
                      return (
                        <Col md="6" key={attribute.attribute_id}>
                          <FormGroup>
                            <Label>{attributeName(attribute)} *</Label>
                            {values.length ? (
                              <Input
                                type="select"
                                value={
                                  attributeValues[attribute.attribute_id] || ""
                                }
                                onChange={event =>
                                  setAttributeValues(current => ({
                                    ...current,
                                    [attribute.attribute_id]: event.target.value
                                  }))
                                }
                              >
                                <option value="">—</option>
                                {values.map(value => (
                                  <option
                                    key={value.value_id}
                                    value={value.value_id}
                                  >
                                    {attributeValueName(value)}
                                  </option>
                                ))}
                              </Input>
                            ) : (
                              <Input
                                value={
                                  attributeValues[attribute.attribute_id] || ""
                                }
                                onChange={event =>
                                  setAttributeValues(current => ({
                                    ...current,
                                    [attribute.attribute_id]: event.target.value
                                  }))
                                }
                              />
                            )}
                          </FormGroup>
                        </Col>
                      );
                    })}
                </Row>
              </div>
            ) : null}
            {!isUpdate ? (
              <FormGroup>
                <Label><T id="shopee.push.form.status" /></Label>
                <Input
                  type="select"
                  value={form.item_status}
                  onChange={event => setField("item_status", event.target.value)}
                >
                  <option value="NORMAL">NORMAL</option>
                  <option value="UNLIST">UNLIST</option>
                </Input>
              </FormGroup>
            ) : (
              <FormGroup>
                <Label><T id="shopee.workspace.image-urls" /></Label>
                <Input
                  type="textarea"
                  rows="3"
                  value={form.image_urls}
                  onChange={event => {
                    setField("image_urls", event.target.value);
                    setImageUrlsDirty(true);
                  }}
                />
                <small className="text-muted">
                  <T id="shopee.workspace.image-help" />
                </small>
              </FormGroup>
            )}
          </React.Fragment>
        ) : null}
      </ModalBody>
      <ModalFooter>
        <Button color="secondary" onClick={onClose}>
          <T id="shopee.cancel" />
        </Button>
        <Button
          color="primary"
          disabled={submitDisabled}
          onClick={submit}
        >
          {busy ? (
            <span className="spinner-border spinner-border-sm mr-2" role="status" />
          ) : null}
          <T
            id={
              isUpdate
                ? "shopee.workspace.update-listing"
                : "shopee.push.btn.push"
            }
          />
        </Button>
      </ModalFooter>
    </Modal>
  );
}

export default injectIntl(ShopeeListingEditor);
